// CardExpansivo.js — Card EXCLUSIVO da faixa "Você poderia gostar".
//
// A DIFERENÇA PARA O CARD DO RESTO DO SITE:
// só a CAPA muda. Em repouso ela é um quadrado (recorte da capa 3:4); com o
// cursor em cima ela se abre até a capa inteira e surgem os botões de lista de
// desejos e de carrinho. Título, autor, nota e preço continuam embaixo da
// capa, visíveis o tempo todo, exatamente como no `CardDestaque` — e o
// carrinho fica no mesmo lugar de sempre: pendurado na borda inferior do
// card, metade dentro e metade fora.
//
// A REGRA QUE DITOU O DESENHO: crescer não pode mexer em nada.
// O card é `position: absolute` dentro de um bloco que reserva a altura do
// estado FECHADO. Esse bloco nunca muda de tamanho, então nem os cards
// vizinhos nem as seções de baixo se deslocam — a capa cresce por cima. A
// folga vem de um `padding-bottom` na lista do carrossel
// (`.secao-expansiva .slick-list`, no App.css), que fica DENTRO da área de
// recorte do overflow: por isso a expansão não é cortada.
//
// EM APARELHO DE TOQUE O EFEITO NÃO EXISTE: sem cursor não há hover, e um card
// que só abre no hover seria um card permanentemente cortado. Ali ele nasce
// aberto (capa inteira, botões visíveis) — mesma guarda `@media (hover: hover)`
// que o resto do site usa.
import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import TextoTruncado from "../../utils/TruncarTexto";
import NotaLivro from "../paginaProduto/NotaLivro";
import { CarrinhoContext } from "../../../services/context/CarrinhoContext";
import { useModal } from "../../../services/context/ModalContext";
import { camposDePreco } from "../../../utils/precoLivro";
import PrecoComDesconto from "../../ui/PrecoComDesconto";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

const CardExpansivo = ({ livro }) => {
  const { id, titulo, image, preco, autores, quantidade, frete } = livro;
  const { adicionarItem, toggleDesejo, estaNaLista } = useContext(CarrinhoContext);
  const { openModal } = useModal();

  const [capaCarregada, setCapaCarregada] = useState(false);
  const isFav = estaNaLista ? estaNaLista(id) : false;

  const precoValido = preco && !isNaN(parseFloat(preco)) && parseFloat(preco) > 0;
  // Liberado pela editora — ver CardDestaque.js, mesma regra e mesma origem.
  const copiaGratis = livro?.copia_gratis_ativa === true;

  // Autores podem vir como objeto ou string, e há livros com `[null]` na base.
  const primeiroAutor = Array.isArray(autores) ? autores.find((a) => a != null) : null;
  const autorNome = primeiroAutor
    ? typeof primeiroAutor === "object"
      ? primeiroAutor.nome
      : primeiroAutor
    : null;

  const aoFavoritar = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (toggleDesejo) {
      // O preço que se cobra, e o "de" junto — como o CardDestaque faz. Salvar
      // o cheio deixava a Minha Lista anunciando um valor que a loja não
      // pratica para quem está na vitrine de destaques.
      toggleDesejo({
        id, titulo, image, ...camposDePreco(livro), autores, formato: "ebook",
        avaliacoes_avg_nota: livro.avaliacoes_avg_nota,
        avaliacoes_count: livro.avaliacoes_count,
      });
    }
  };

  const aoAdicionar = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!precoValido) return;
    // `formato` do item = QUAL VERSÃO está sendo comprada, e todo card vende
    // o digital (usa `livro.preco`; o impresso é `preco_impresso`, escolhido
    // só no PrecoCard). NUNCA repassar `livro.formato` aqui: ali o campo é o
    // tamanho do impresso ("16x23 (160x230)") e foi o que quebrou o checkout
    // em 20/08/2026. O CarrinhoContext também saneia, mas a intenção fica
    // explícita na origem.
      // A nota e o autor viajam junto com o item para o carrinho poder
      // mostrá-los sem uma requisição por linha. Quem já tinha itens no
      // carrinho antes disto não tem os campos, e lá a linha de estrelas
      // simplesmente não aparece (ver a guarda em CarrinhoPage.js).
    const item = {
      // O preco que vai para o carrinho e o do SERVIDOR (precoDeVenda le
      // `preco_com_desconto`). Mandar o cheio de um livro em destaque faz o
      // checkout recusar o pedido inteiro -- ver src/utils/precoLivro.js.
      // `preco_original` viaja junto para o carrinho poder riscar o valor antigo.
      id, titulo, ...camposDePreco(livro), image, autores, formato: "ebook",
      quantidade: quantidade || 1,
      avaliacoes_avg_nota: livro.avaliacoes_avg_nota,
      avaliacoes_count: livro.avaliacoes_count,
    };
    if (frete) item.frete = frete;
    adicionarItem(item);
    openModal(item);
  };

  const irAoTopo = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    // Bloco que RESERVA a altura do card fechado (capa quadrada + ficha).
    // É ele que ocupa espaço na faixa; o card por cima cresce sem empurrar.
    <div className="carrossel-expansivo px-2 pb-1">
      <div
        className="cartao cartao-clique cartao-expansivo group/exp relative"
      >
        {/* ===== Capa: o único elemento que muda de tamanho =====
            Quadrada em repouso, 3:4 no hover. A altura vem de
            `padding-bottom` (100% → 133,33%), e não de `aspect-ratio`,
            porque porcentagem de padding anima em qualquer navegador — o
            `aspect-ratio` ainda não. */}
        {/* O `overflow-hidden` mora AQUI, e não no card: é a capa que precisa
            recortar a imagem. No card, ele cortaria pela metade o botão de
            carrinho, que é pendurado na borda de baixo. O arredondamento
            acompanha o do card (3px em `.cartao`), um pixel menor por causa
            da borda de 2px. */}
        <div className="capa-expansivo bg-insigne-navy-deep overflow-hidden rounded-t-[2px]">
          {!capaCarregada && <div className="absolute inset-0 animate-pulse bg-insigne-navy-mid/60" />}
          <img
            src={`${API_URL}public/${image}`}
            alt={titulo}
            loading="lazy"
            onLoad={() => setCapaCarregada(true)}
            onError={() => setCapaCarregada(true)}
            // `object-top`: o recorte quadrado tira o pé da capa, onde costuma
            // ficar o selo da editora. Cortar por cima levaria embora o título
            // impresso, que é o que identifica o livro de longe.
            className={
              "absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-300 " +
              (capaCarregada ? "opacity-100" : "opacity-0")
            }
          />

          {/* O link cobre a capa inteira, por baixo dos botões. */}
          <Link
            to={main + "livros/" + id}
            onClick={irAoTopo}
            aria-label={`Ver ${titulo}`}
            className="absolute inset-0 z-10"
          />

          {/* Coração de vidro — o mesmo do CardDestaque: contorno e sombra na
              MESMA cor (branco translúcido), porque ele fica sobre a capa e
              qualquer outra cor viraria um elemento solto em cima do livro. */}
          <button
            onClick={aoFavoritar}
            aria-label={isFav ? "Remover da Minha Lista" : "Salvar na Minha Lista"}
            aria-pressed={isFav}
            title={isFav ? "Remover da Minha Lista" : "Salvar na Minha Lista"}
            className="acao-expansivo absolute top-2.5 right-2.5 z-20 inline-flex items-center justify-center
                       w-10 h-10 rounded-xl border-2 border-paper/45
                       bg-paper/30 backdrop-blur-sm
                       shadow-[3px_3px_0_0_rgb(255_255_255_/_0.45)]
                       transition-[box-shadow,background-color,border-color] duration-150
                       hover:bg-paper/55 hover:border-paper/60
                       hover:shadow-[5px_5px_0_0_rgb(255_255_255_/_0.6)]"
          >
            <i className={"bi text-sm " + (isFav ? "bi-heart-fill text-red-500" : "bi-heart text-ink-fixed")} />
          </button>

        </div>

        {/* ===== Ficha: não muda no hover =====
            Altura FIXA (não `min-h`). O bloco reservado lá fora conta com este
            número exato para saber quanto espaço guardar; e, como no
            CardDestaque, altura fixa é o que mantém a linha da faixa reta
            quando um título tem uma linha e o vizinho tem duas. */}
        <div className="ficha-expansiva px-3 py-3">
          <h3 className="text-white font-bold leading-snug text-sm h-10 line-clamp-2 overflow-hidden">
            <TextoTruncado texto={titulo} limite={40} />
          </h3>
          <p className="text-white/55 text-xs mt-1 h-4 truncate">{autorNome || ""}</p>
          <div className="h-5 flex items-center mt-1">
            <NotaLivro livro={livro} size="text-[11px]" compacto />
          </div>
          {/* O preço, riscado em cima e selo à direita — o desenho de toda
              vitrine (ver PrecoComDesconto). ALTURA FIXA nos dois ramos: são
              os 44px que `--ficha-expansiva` (App.css) conta, 16 do riscado e
              28 do preço. Por isso também `quebrar={false}`: o selo que
              descesse de linha vazaria por baixo da ficha. O card nunca fica
              estreito o bastante para isso (≥ 200px de ficha em toda largura
              do carrossel). */}
          {copiaGratis ? (
            // A MESMA ALTURA de 44px dos outros dois ramos: a ficha do card
            // expansivo tem altura contada no App.css (`--ficha-expansiva`), e
            // um bloco mais curto aqui encolheria a capa de todo o carrossel.
            <p className="mt-1 h-11 flex items-end text-xl font-bold text-insigne-promo">Grátis</p>
          ) : precoValido ? (
            <PrecoComDesconto
              livro={livro}
              numero="text-xl"
              quebrar={false}
              linha="h-7"
              className="mt-1 h-11 text-white/90"
            />
          ) : (
            <p className="text-sm text-white/50 mt-1 h-11 flex items-end">Disponível em físico</p>
          )}
        </div>

        {/* Carrinho pendurado na borda inferior do card: metade dentro, metade
            fora — o mesmo lugar do CardDestaque, para o botão de comprar estar
            no mesmo canto em todo o site. Fica FORA do fluxo (absolute), então
            não ocupa altura e não desalinha a linha da faixa. */}
        {/* Sem carrinho no título liberado: não há o que adicionar, e o card
            inteiro já leva para a página onde se lê e se baixa. */}
        {precoValido && !copiaGratis && (
          <button
            onClick={aoAdicionar}
            aria-label={`Adicionar ${titulo} ao carrinho`}
            title="Adicionar ao carrinho"
            className="acao-expansivo group/cart absolute left-1/2 bottom-0 z-20
                       -translate-x-1/2 translate-y-1/2
                       inline-flex items-center justify-center w-14 h-11 rounded-xl
                       border-2 border-insigne-orange-dark bg-insigne-orange text-paper
                       shadow-[3px_3px_0_0_rgb(var(--c-orange-dark))]
                       transition-[box-shadow,background-color] duration-150
                       hover:bg-insigne-orange-alt hover:shadow-[5px_5px_0_0_rgb(var(--c-orange-dark))]"
          >
            {/* Carrinho vazio em repouso, cheio com o cursor no botão: o ícone
                antecipa o que o clique faz. */}
            <i className="bi bi-cart text-base group-hover/cart:hidden" />
            <i className="bi bi-cart-fill text-base hidden group-hover/cart:inline" />
          </button>
        )}
      </div>
    </div>
  );
};

export default CardExpansivo;
